import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  Brain,
  Calculator,
  Command,
  Gauge,
  Grid3X3,
  Radio,
  Send,
  Shield,
  Target,
  Zap,
} from "lucide-react";
import BloombergSupportScanner from "./BloombergSupportScanner";
import BloombergSensexSupportScanner from "./BloombergSensexSupportScanner";

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";
const TOKEN_KEY = "ct_login_token";
const TERMINAL_FETCH_KEY = "bloomberg_terminal_fetch";
const NEXO_API = window.location.hostname === "localhost"
  ? "http://localhost:5000/api/nexo"
  : "/api/nexo";

const C = {
  gold: "#f7c948",
  dimGold: "#9f7a24",
  dark: "#020202",
  panel: "#070604",
  card: "#0b0905",
  line: "rgba(247,201,72,0.26)",
  green: "#22c55e",
  red: "#ef4444",
  emerald: "#064e3b",
  maroon: "#4a0712",
  activeTab: "#006b49",
};

function biasColor(bias) {
  if (bias === "BULLISH") return C.green;
  if (bias === "BEARISH") return C.red;
  return C.gold;
}

function ModuleCard({ icon, title, children }) {
  return (
    <div className="rounded-lg border p-4" style={{ background: C.card, borderColor: C.line }}>
      <div className="flex items-center gap-2 text-sm font-bold tracking-[0.22em]" style={{ color: C.gold }}>
        {icon}
        {title}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

export default function BloombergTerminal() {
  const [snapshot, setSnapshot] = useState(null);
  const [source, setSource] = useState("DEMO");
  const [bloombergRefreshSec, setBloombergRefreshSec] = useState(1);
  const [refreshCountdown, setRefreshCountdown] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchEnabled, setFetchEnabled] = useState(() => {
    return localStorage.getItem(TERMINAL_FETCH_KEY) !== "STOP";
  });
  const [terminalTab, setTerminalTab] = useState("overview");
  const [scannerSettings, setScannerSettings] = useState({
    nifty: { quantity: 50, slPoints: 15, candleMinutes: 1 },
    banknifty: { quantity: 50, slPoints: 15, candleMinutes: 1 },
    sensex: { quantity: 50, slPoints: 15, candleMinutes: 1 },
    bankex: { quantity: 50, slPoints: 15, candleMinutes: 1 },
    manualSupportRanges: {},
  });
  const [command, setCommand] = useState("");
  const [commandResult, setCommandResult] = useState("");
  const [alertText, setAlertText] = useState("");
  const [alerts, setAlerts] = useState([]);
  const [nexoGroups, setNexoGroups] = useState([]);
  const [selectedNexoGroupId, setSelectedNexoGroupId] = useState("");
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [broadcasting, setBroadcasting] = useState(false);

  const toggleFetching = () => {
    const next = !fetchEnabled;

    setFetchEnabled(next);

    localStorage.setItem(
      TERMINAL_FETCH_KEY,
      next ? "START" : "STOP"
    );
  };

  const loadSnapshot = async () => {
    const token = localStorage.getItem(TOKEN_KEY);

    try {
      let settings = {};

      if (token) {
        const settingsRes = await fetch(`${API_BASE}/api/login/admin/secret-settings`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
        });

        const settingsData = await settingsRes.json().catch(() => ({}));
        settings = settingsData.settings || {};

        const nextRefresh = Math.max(1, Number(settings.bloombergRefreshSeconds || 1));
        setBloombergRefreshSec(nextRefresh);

        setScannerSettings({
          nifty: {
            quantity: Math.max(1, Number(settings.bloombergScannerNiftyQty || 50)),
            slPoints: Math.max(1, Number(settings.bloombergScannerNiftySlPoints || 15)),
            candleMinutes: Number(settings.bloombergScannerNiftyCandleMinutes || 1),
          },
          banknifty: {
            quantity: Math.max(1, Number(settings.bloombergScannerBankNiftyQty || 50)),
            slPoints: Math.max(1, Number(settings.bloombergScannerBankNiftySlPoints || 15)),
            candleMinutes: Number(settings.bloombergScannerBankNiftyCandleMinutes || 1),
          },
          sensex: {
            quantity: Math.max(1, Number(settings.bloombergScannerSensexQty || 50)),
            slPoints: Math.max(1, Number(settings.bloombergScannerSensexSlPoints || 15)),
            candleMinutes: Number(settings.bloombergScannerSensexCandleMinutes || 1),
          },
          bankex: {
            quantity: Math.max(1, Number(settings.bloombergScannerBankexQty || 50)),
            slPoints: Math.max(1, Number(settings.bloombergScannerBankexSlPoints || 15)),
            candleMinutes: Number(settings.bloombergScannerBankexCandleMinutes || 1),
          },
          manualSupportRanges: settings.bloombergManualSupportRanges || {},
        });
      }

      const res = await fetch(`${API_BASE}/api/bloomberg/snapshot?t=${Date.now()}`, {
        cache: "no-store",
      });
      const data = await res.json().catch(() => ({}));

      if (data.success) {
        setSnapshot(data);
        setSource("NSE/BSE");
      }
    } catch {
      const res = await fetch(`${API_BASE}/api/bloomberg/snapshot?t=${Date.now()}`, {
        cache: "no-store",
      });
      const data = await res.json().catch(() => ({}));

      if (data.success) {
        setSnapshot(data);
        setSource("NSE/BSE");
      }
    }
  };

  const loadAlerts = async () => {
    const res = await fetch(`${API_BASE}/api/bloomberg/alerts`);
    const data = await res.json().catch(() => ({}));
    if (data.success) setAlerts(data.alerts || []);
  };

  useEffect(() => {
    const runRefresh = async () => {

      if (!fetchEnabled) return;

      setRefreshing(true);
      await Promise.all([
        loadSnapshot().catch(() => {}),
        loadAlerts().catch(() => {}),
      ]);
      setRefreshCountdown(bloombergRefreshSec);
      setRefreshing(false);
    };

    runRefresh();

    let refreshTimer;
    let countdownTimer;

    if (fetchEnabled) {
      runRefresh();

      refreshTimer = setInterval(runRefresh, bloombergRefreshSec * 1000);

      countdownTimer = setInterval(() => {
        setRefreshCountdown(prev =>
          prev <= 1
            ? bloombergRefreshSec
            : prev - 1
        );
      }, 1000);
    }

    return () => {
      if (refreshTimer) clearInterval(refreshTimer);
      if (countdownTimer) clearInterval(countdownTimer);
    };
  }, [bloombergRefreshSec, fetchEnabled]);

  const loadNexoGroups = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;

    const res = await fetch(`${NEXO_API}/groups`, {
        headers: { Authorization: `Bearer ${token}` },
    });

    const data = await res.json().catch(() => ({}));

    if (res.ok && data.success) {
        const groups = data.groups || [];
        setNexoGroups(groups);

        if (!selectedNexoGroupId && groups[0]) {
        setSelectedNexoGroupId(groups[0].id);
        }
    }
    }, [selectedNexoGroupId]);

    useEffect(() => {
      loadNexoGroups().catch(() => {});
    }, [loadNexoGroups]);

  const indices = useMemo(() => snapshot?.indices || [], [snapshot?.indices]);

  const runCommand = async e => {
    e.preventDefault();
    if (!command.trim()) return;

    const res = await fetch(`${API_BASE}/api/bloomberg/command`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ command }),
    });

    const data = await res.json().catch(() => ({}));
    setCommandResult(data.response || "Command failed");
    setCommand("");
  };

  const createAlert = async () => {
    if (!alertText.trim()) return;

    const res = await fetch(`${API_BASE}/api/bloomberg/alerts`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: alertText }),
    });

    const data = await res.json().catch(() => ({}));
    if (data.success) {
      setAlerts(data.alerts || []);
      setAlertText("");
    }
  };

  const broadcastTopSignal = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const plan = snapshot?.tradePlan;

    if (!token) {
        setBroadcastMessage("Login token missing");
        return;
    }

    if (!selectedNexoGroupId) {
        setBroadcastMessage("Select Nexo group");
        return;
    }

    if (!plan) {
        setBroadcastMessage("No trade plan available");
        return;
    }

    setBroadcasting(true);
    setBroadcastMessage("");

    try {
        const signalPayload = {
        side: plan.side === "PE" ? "SELL" : "BUY",
        symbol: `${plan.index} ${plan.side} ${plan.strike}`,
        entry: String(plan.entry),
        stopLoss: String(plan.stopLoss),
        target: `${plan.target1} / ${plan.target2}`,
        note: `Confidence ${plan.confidence}/100 | Qty ${plan.qty} | Max Loss Rs.${plan.maxLoss} | R:R ${plan.rr1}/${plan.rr2}`,
        };

        const res = await fetch(`${NEXO_API}/groups/${selectedNexoGroupId}/signals`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(signalPayload),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok || !data.success) {
        throw new Error(data.error || "Broadcast failed");
        }

        setBroadcastMessage("Signal broadcasted to Nexo");
    } catch (err) {
        setBroadcastMessage(err.message || "Broadcast failed");
    } finally {
        setBroadcasting(false);
    }
    }; 

  return (
    <div className="min-h-screen p-5" style={{ background: C.dark, color: C.gold }}>

      <div className="flex flex-wrap items-center justify-between gap-4 border-b pb-4" style={{ borderColor: C.line }}>
        <div>
          <div className="text-xs tracking-[0.45em]" style={{ color: C.dimGold }}>
            NEXOVATE MARKET COMMAND CENTER
          </div>
          <h1 className="mt-2 text-3xl font-bold">
            Welcome to the Bloomberg Terminal
          </h1>
        </div>

        <div className="flex flex-col items-end gap-3 text-right text-xs tracking-[0.22em]">
        <div>
          <div style={{ color: C.dimGold }}>UPDATED</div>
          <div>{snapshot?.updatedAt ? new Date(snapshot.updatedAt).toLocaleTimeString() : "--:--:--"}</div>
          <div style={{ color: source === "LIVE" ? C.green : C.dimGold }}>
            {source}
          </div>
          <div className="mt-1" style={{ color: C.dimGold }}>
            AUTO {bloombergRefreshSec}s · NEXT {refreshCountdown}s
          </div>
        </div>

        <button
          type="button"
          onClick={toggleFetching}
          className="rounded px-4 py-2 font-bold"
          style={{
            background: fetchEnabled ? "#8b0000" : "#006b49",
            color: "#fff",
            border: `1px solid ${fetchEnabled ? "#ff5555" : "#22c55e"}`
          }}
        >
          {fetchEnabled ? "STOP" : "START"}
        </button>

        <button
          type="button"
          onClick={async () => {
            setRefreshing(true);
            await Promise.all([
              loadSnapshot().catch(() => {}),
              loadAlerts().catch(() => {}),
            ]);
            setRefreshCountdown(bloombergRefreshSec);
            setRefreshing(false);
          }}
          className="terminal-tab-active rounded px-4 py-2 font-bold"
          style={{
            background: C.activeTab,
            color: "#ffffff",
            border: `1px solid ${C.green}`,
            boxShadow: "0 0 16px rgba(0,107,73,0.75)",
          }}
        >
          {refreshing ? "Refreshing" : "Refresh"}
        </button>

        <div className="flex flex-wrap justify-end gap-2">
          {[
            { id: "overview", label: "Overview" },
            { id: "support-scanner", label: "Support Scanner" },
            { id: "sensex-support-scanner", label: "Sensex Scanner" },
          ].map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setTerminalTab(tab.id)}
              className={`rounded px-4 py-2 text-sm font-bold ${terminalTab === tab.id ? "terminal-tab-active" : ""}`}
              style={{
                background: terminalTab === tab.id ? C.activeTab : "#020202",
                border: `1px solid ${terminalTab === tab.id ? C.green : C.line}`,
                color: terminalTab === tab.id ? "#ffffff" : C.gold,
                boxShadow: terminalTab === tab.id ? "0 0 16px rgba(0,107,73,0.75)" : "none",
              }}
            >
              {tab.label}
            </button>
          ))}
          </div>
      </div>
    </div>

      {terminalTab === "support-scanner" && (
        <BloombergSupportScanner snapshot={snapshot} scannerSettings={scannerSettings} />
      )}

      {terminalTab === "sensex-support-scanner" && (
        <BloombergSensexSupportScanner snapshot={snapshot} scannerSettings={scannerSettings} />
      )}

      {terminalTab === "overview" && (
        <>
          <form onSubmit={runCommand} className="mt-5 flex gap-3 rounded-lg border p-3" style={{ background: C.panel, borderColor: C.line }}>

        <Command size={22} />
        <input
          value={command}
          onChange={e => setCommand(e.target.value)}
          placeholder="Type command: scan nifty ce, open risk, alert banknifty..."
          className="flex-1 bg-transparent outline-none"
          style={{ color: C.gold }}
        />
        <button
            className="terminal-tab-active rounded px-4 py-2 text-sm font-bold"
          style={{ background: `linear-gradient(180deg, ${C.emerald}, #022c22)`, color: "#d1fae5" }}
        >
          RUN
        </button>
      </form>

      {commandResult && (
        <div className="mt-3 rounded border px-4 py-2 text-sm" style={{ background: C.card, borderColor: C.line }}>
          {commandResult}
        </div>
      )}

      <div className="mt-5 grid gap-4 xl:grid-cols-4 md:grid-cols-2">
        {indices.map(item => (
          <div key={item.key} className="rounded-lg border p-4" style={{ background: C.card, borderColor: C.line }}>
            <div className="flex items-center justify-between">
              <div className="text-xl font-bold">{item.label}</div>
              <div className="text-xs" style={{ color: biasColor(item.bias) }}>{item.bias}</div>
            </div>
            <div className="mt-3 text-3xl font-bold">{Number(item.spot).toLocaleString("en-IN")}</div>
            <div className="mt-3 flex items-center justify-between text-sm">
              <span>{item.pressure}</span>
              <span>{item.confidence}/100</span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded bg-black">
              <div
                className="h-full"
                style={{
                  width: `${item.confidence}%`,
                  background: biasColor(item.bias),
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-4">
        <ModuleCard icon={<Activity size={18} />} title="MARKET REGIME ENGINE">
          <div className="grid gap-3">
            {Object.entries(snapshot?.marketRegimes || {}).map(([key, regime]) => (
              <div
                key={key}
                className="rounded border p-3"
                style={{ borderColor: C.line, background: "#020202" }}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="text-lg font-bold">
                    {key.toUpperCase()}
                  </div>

                  <div
                    className="rounded px-3 py-1 text-xs font-bold tracking-[0.18em]"
                    style={{
                      color: biasColor(regime.bias),
                      border: `1px solid ${biasColor(regime.bias)}`,
                      boxShadow: `0 0 10px ${biasColor(regime.bias)}55`,
                    }}
                  >
                    {regime.bias} · {regime.confidence}
                  </div>
                </div>

                <div className="mt-2 text-sm font-bold">
                  {regime.pressure}
                </div>

                <div className="mt-2 text-xs leading-5" style={{ color: C.dimGold }}>
                  {regime.reason}
                </div>
              </div>
            ))}
          </div>
        </ModuleCard>

        <ModuleCard icon={<Target size={18} />} title="SIGNAL CONFIDENCE">
          {(snapshot?.signals || []).map(sig => (
            <div key={sig.id} className="rounded border p-3" style={{ borderColor: C.line }}>
              <div className="flex justify-between font-bold">
                <span>{sig.index} {sig.side} {sig.strike}</span>
                <span>{sig.confidence}/100</span>
              </div>
              <div className="mt-2 text-sm">{sig.reason}</div>
            </div>
          ))}
        </ModuleCard>

        <ModuleCard icon={<Shield size={18} />} title="RISK COMMAND">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>Day P&L: ₹{Number(snapshot?.risk?.dayPnl || 0).toLocaleString("en-IN")}</div>
            <div>Open Risk: ₹{Number(snapshot?.risk?.openRisk || 0).toLocaleString("en-IN")}</div>
            <div>Open Orders: {snapshot?.risk?.openOrders || 0}</div>
            <div>Open Positions: {snapshot?.risk?.openPositions || 0}</div>
            <div>Rejected: {snapshot?.risk?.rejectedOrders || 0}</div>
            <div>Mismatch: {snapshot?.risk?.parentMismatch ? "YES" : "NO"}</div>
         </div>
        </ModuleCard>

        <ModuleCard icon={<Calculator size={18} />} title="TRADE PLAN ENGINE">
        {snapshot?.tradePlan ? (
            <div className="grid grid-cols-2 gap-3 text-sm">
            <div>{snapshot.tradePlan.index} {snapshot.tradePlan.side} {snapshot.tradePlan.strike}</div>
            <div>Confidence: {snapshot.tradePlan.confidence}/100</div>
            <div>Entry: ₹{snapshot.tradePlan.entry}</div>
            <div>SL: ₹{snapshot.tradePlan.stopLoss}</div>
            <div>T1: ₹{snapshot.tradePlan.target1}</div>
            <div>T2: ₹{snapshot.tradePlan.target2}</div>
            <div>Qty: {snapshot.tradePlan.qty}</div>
            <div>Max Loss: ₹{snapshot.tradePlan.maxLoss}</div>
            <div>R:R 1: {snapshot.tradePlan.rr1}</div>
            <div>R:R 2: {snapshot.tradePlan.rr2}</div>
            </div>
        ) : (
            <div className="text-sm" style={{ color: C.dimGold }}>
            Waiting for scanner signal.
            </div>
        )}
        </ModuleCard>

      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-4 md:grid-cols-2">
        <ModuleCard icon={<Grid3X3 size={18} />} title="MULTI-INDEX HEATMAP">
          <div className="grid grid-cols-2 gap-2 text-sm">
            {indices.map(i => (
              <div key={i.key} className="rounded border p-2" style={{ borderColor: C.line, color: biasColor(i.bias) }}>
                {i.label}: {i.confidence}
              </div>
            ))}
          </div>
        </ModuleCard>

        <ModuleCard icon={<AlertTriangle size={18} />} title="ALERT ENGINE">
          <div className="flex gap-2">
            <input
              value={alertText}
              onChange={e => setAlertText(e.target.value)}
              placeholder="Alert condition"
              className="min-w-0 flex-1 rounded border px-3 py-2"
              style={{ background: "#020202", borderColor: C.line }}
            />
            <button
              type="button"
              onClick={createAlert}
              className="rounded px-3"
              style={{ background: `linear-gradient(180deg, ${C.emerald}, #022c22)` }}
            >
              <Zap size={16} />
            </button>
          </div>
          <div className="mt-3 space-y-2 text-xs">
            {alerts.slice(0, 3).map(a => <div key={a.id}>{a.text}</div>)}
          </div>
        </ModuleCard>

        <ModuleCard icon={<Gauge size={18} />} title="REPLAY MODE">
          <div className="text-sm">Replay engine shell ready.</div>
          <div className="mt-2 text-xs" style={{ color: C.dimGold }}>
            Next: replay scanner/OI from selected time window.
          </div>
        </ModuleCard>

        <ModuleCard icon={<Brain size={18} />} title="AI ANALYST">
          <div className="text-sm">
            Ask: why bearish, best setup, summarize OI shift, find strongest support.
          </div>
        </ModuleCard>
      </div>

        <div className="mt-5 grid gap-4 xl:grid-cols-2">
  {["nifty", "banknifty", "sensex", "bankex"].map(key => {
    const chain = snapshot?.optionChains?.[key];
    if (!chain) return null;

    return (
      <ModuleCard
        key={key}
        icon={<Grid3X3 size={18} />}
        title={`${chain.index} OPTION CHAIN`}
      >
        <div className="grid grid-cols-4 gap-3 text-sm">
          <div>
            <div style={{ color: C.dimGold }}>Spot</div>
            <div className="text-xl font-bold">{Number(chain.spot || 0).toLocaleString("en-IN")}</div>
          </div>
          <div>
            <div style={{ color: C.dimGold }}>Expiry</div>
            <div className="text-xl font-bold">{chain.expiry || "--"}</div>
          </div>
          <div>
            <div style={{ color: C.dimGold }}>PCR</div>
            <div className="text-xl font-bold">{chain.pcr || 0}</div>
          </div>
          <div>
            <div style={{ color: C.dimGold }}>Rows</div>
            <div className="text-xl font-bold">{chain.rowsCount || 0}</div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded border p-3" style={{ borderColor: C.line }}>
            <div className="mb-2 text-sm font-bold" style={{ color: C.red }}>
              TOP RESISTANCE CE OI
            </div>
            {(chain.topResistance || []).map(row => (
              <div key={`ce-${row.strike}`} className="flex justify-between text-sm">
                <span>{row.strike}</span>
                <span>{Number(row.oi || 0).toLocaleString("en-IN")}</span>
              </div>
            ))}
          </div>

          <div className="rounded border p-3" style={{ borderColor: C.line }}>
            <div className="mb-2 text-sm font-bold" style={{ color: C.green }}>
              TOP SUPPORT PE OI
            </div>
            {(chain.topSupport || []).map(row => (
              <div key={`pe-${row.strike}`} className="flex justify-between text-sm">
                <span>{row.strike}</span>
                <span>{Number(row.oi || 0).toLocaleString("en-IN")}</span>
              </div>
            ))}
          </div>
        </div>
      </ModuleCard>
    );
  })}
</div>

      <div className="mt-5 rounded-lg border p-4" style={{ background: C.panel, borderColor: C.line }}>
        <div className="flex items-center gap-2 font-bold tracking-[0.22em]">
          <Radio size={18} /> NEXO SIGNAL DESK
        </div>
        <div className="mt-3 grid gap-3 xl:grid-cols-[260px_1fr_auto]">
            <select
                value={selectedNexoGroupId}
                onChange={e => setSelectedNexoGroupId(e.target.value)}
                className="rounded border px-4 py-3"
                style={{ background: "#020202", borderColor: C.line }}
            >
                <option value="">Select Nexo group</option>
                {nexoGroups.map(group => (
                <option key={group.id} value={group.id}>
                    {group.name}
                </option>
                ))}
            </select>

            <div className="rounded border px-4 py-3 text-sm" style={{ background: "#020202", borderColor: C.line }}>
                {snapshot?.tradePlan
                ? `${snapshot.tradePlan.index} ${snapshot.tradePlan.side} ${snapshot.tradePlan.strike} | Entry ${snapshot.tradePlan.entry} | SL ${snapshot.tradePlan.stopLoss} | T1 ${snapshot.tradePlan.target1} | T2 ${snapshot.tradePlan.target2}`
                : "No trade plan available"}
            </div>

            <button
                type="button"
                onClick={broadcastTopSignal}
                disabled={broadcasting}
                className="flex items-center justify-center gap-2 rounded px-5 font-bold disabled:opacity-50"
                style={{ background: `linear-gradient(180deg, ${C.emerald}, #022c22)`, color: "#d1fae5" }}
            >
                <Send size={18} /> {broadcasting ? "Sending..." : "Broadcast Signal"}
            </button>
            </div>

            {broadcastMessage && (
            <div className="mt-3 text-sm" style={{ color: broadcastMessage.includes("broadcasted") ? C.green : C.red }}>
                {broadcastMessage}
            </div>
            )}
            </div>
                </>
      )}
    </div>
  );
}
